"use client";

import React, { useState, useMemo } from "react";
import { MediaItem } from "@/type/commonTypes";
import MediaFeaturedCard from "../cards/MediaFeaturedCard";
import MediaCard from "../cards/MediaCard";
import { Pagination } from "@/components/navigation/Pagination";

interface MediaCoverageSectionProps {
  mediaList: MediaItem[];
}

type FilterType = "all" | "articles" | "videos";

const ITEMS_PER_PAGE = 9;

export default function MediaCoverageSection({
  mediaList,
}: MediaCoverageSectionProps) {
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [currentPage, setCurrentPage] = useState<number>(1);

  const featuredItem = mediaList.length > 0 ? mediaList[0] : null;
  const remainingItems = useMemo(() => {
    return mediaList.length > 1 ? mediaList.slice(1) : [];
  }, [mediaList]);

  // Check which types exist dynamically in the dataset
  const videoCount = useMemo(
    () => remainingItems.filter((i) => i.linkType === "video").length,
    [remainingItems],
  );
  const articleCount = useMemo(
    () => remainingItems.filter((i) => i.linkType !== "video").length,
    [remainingItems],
  );
  const showFilters = videoCount > 0 && articleCount > 0;

  const filteredItems = useMemo(() => {
    if (activeFilter === "videos") {
      return remainingItems.filter((i) => i.linkType === "video");
    }
    if (activeFilter === "articles") {
      return remainingItems.filter((i) => i.linkType !== "video");
    }
    return remainingItems;
  }, [remainingItems, activeFilter]);

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);

  // Paginated items: 9 cards per page
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredItems, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const sectionEl = document.getElementById("latest-coverage-section");
    if (sectionEl) {
      sectionEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!mediaList || mediaList.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#EAECF0] p-10 text-center max-w-xl mx-auto shadow-xs my-12">
        <div className="w-12 h-12 rounded-full bg-blue-50 text-primaryBlue flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
            />
          </svg>
        </div>
        <div className="text-lg font-bold text-primaryBlack mb-1">
          No Media Coverage Yet
        </div>
        <p className="text-sm text-[#667085]">
          Please check back soon for our latest press releases, interviews, and
          media mentions.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* 1. Top Featured Coverage Card */}
      {featuredItem && <MediaFeaturedCard item={featuredItem} />}

      {/* 2. Latest Coverage Section */}
      {remainingItems.length > 0 && (
        <section
          id="latest-coverage-section"
          className="mb-16 sm:mb-24 scroll-mt-24"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <h2 className="text-1.5xl sm:text-2.5xl font-bold text-primaryBlack tracking-tight">
              Latest Coverage
            </h2>

            {/* Dynamic Filters (shown only when multiple media types exist) */}
            {showFilters && (
              <div className="flex items-center gap-2 self-start sm:self-auto overflow-x-auto pb-1 sm:pb-0">
                <button
                  type="button"
                  onClick={() => handleFilterChange("all")}
                  className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                    activeFilter === "all"
                      ? "bg-primaryBlue text-white shadow-xs"
                      : "bg-[#F2F4F7] text-[#475467] hover:bg-[#EAECF0]"
                  }`}
                >
                  All ({remainingItems.length})
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange("articles")}
                  className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                    activeFilter === "articles"
                      ? "bg-primaryBlue text-white shadow-xs"
                      : "bg-[#F2F4F7] text-[#475467] hover:bg-[#EAECF0]"
                  }`}
                >
                  Articles ({articleCount})
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange("videos")}
                  className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition ${
                    activeFilter === "videos"
                      ? "bg-primaryBlue text-white shadow-xs"
                      : "bg-[#F2F4F7] text-[#475467] hover:bg-[#EAECF0]"
                  }`}
                >
                  Videos ({videoCount})
                </button>
              </div>
            )}
          </div>

          {/* Grid of Coverage Cards (9 per page) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {paginatedItems.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-[#EAECF0]">
              <p className="text-[#667085] text-sm">
                No coverage found for this filter category.
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-12 sm:mt-16">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
